/**
 * CRM reminder notifications — browser + Android WebView bridge.
 * Server-side FCM (Backend cron) handles reminders when the tab/app is closed.
 */

const browserTimers = new Map();

export function isAndroidWebView() {
  return typeof window !== "undefined" && Boolean(window.BeentaraAndroid);
}

function parseReminderDateTime(dateStr, timeStr) {
  if (!dateStr || !timeStr) return null;
  const normalizedTime = String(timeStr).trim().slice(0, 5);
  const iso = `${String(dateStr).trim()}T${normalizedTime}:00`;
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? null : parsed.getTime();
}

export async function ensureCrmNotificationPermission() {
  if (isAndroidWebView()) {
    try {
      if (window.BeentaraAndroid.requestNotificationPermission) {
        window.BeentaraAndroid.requestNotificationPermission();
      }
      return window.BeentaraAndroid.hasNotificationPermission?.() === true;
    } catch {
      return false;
    }
  }

  if (typeof Notification === "undefined") return false;
  if (Notification.permission === "granted") return true;
  if (Notification.permission === "denied") return false;
  const result = await Notification.requestPermission();
  return result === "granted";
}

function showBrowserNotification(title, body, leadId) {
  if (typeof Notification === "undefined" || Notification.permission !== "granted") {
    return;
  }

  const options = {
    body,
    icon: "/logo192.png",
    badge: "/logo192.png",
    tag: `crm-reminder-${leadId}`,
    renotify: true,
    data: { leadId, url: "/crm" },
  };

  try {
    if (navigator.serviceWorker?.controller) {
      navigator.serviceWorker.ready.then((reg) => {
        reg.showNotification(title, options);
      });
      return;
    }
    new Notification(title, options);
  } catch {
    /* ignore */
  }
}

function scheduleBrowserTimer(leadId, title, body, triggerAtMs) {
  const delay = triggerAtMs - Date.now();
  if (delay <= 0 || delay > 2147483647) return;

  if (browserTimers.has(String(leadId))) {
    clearTimeout(browserTimers.get(String(leadId)));
  }

  const timerId = setTimeout(() => {
    browserTimers.delete(String(leadId));
    showBrowserNotification(title, body, leadId);
  }, delay);

  browserTimers.set(String(leadId), timerId);
}

export function cancelCrmReminder(leadId) {
  const id = String(leadId);
  if (browserTimers.has(id)) {
    clearTimeout(browserTimers.get(id));
    browserTimers.delete(id);
  }

  if (isAndroidWebView() && window.BeentaraAndroid?.cancelCrmReminder) {
    try {
      window.BeentaraAndroid.cancelCrmReminder(id);
    } catch {
      /* ignore */
    }
  }
}

export function scheduleCrmReminder(lead) {
  if (!lead?.id || !lead.remainder_date || !lead.remainder_time) return false;

  const triggerAtMs = parseReminderDateTime(lead.remainder_date, lead.remainder_time);
  if (!triggerAtMs || triggerAtMs <= Date.now()) return false;

  const leadName = lead.name || lead.mobile || "Lead";
  const title = `CRM Reminder: ${leadName}`;
  const body =
    lead.followup_description?.trim() ||
    `Follow up with ${leadName}${lead.mobile ? ` (${lead.mobile})` : ""}`;

  if (isAndroidWebView() && window.BeentaraAndroid?.scheduleCrmReminder) {
    try {
      window.BeentaraAndroid.scheduleCrmReminder(
        JSON.stringify({
          id: String(lead.id),
          leadId: String(lead.id),
          title,
          body,
          atEpochMs: triggerAtMs,
        })
      );
      return true;
    } catch {
      return false;
    }
  }

  scheduleBrowserTimer(lead.id, title, body, triggerAtMs);
  return true;
}

export async function syncCrmRemindersFromLeads(leads = []) {
  if (!Array.isArray(leads) || !leads.length) return;

  const granted = await ensureCrmNotificationPermission();
  if (!granted && !isAndroidWebView()) return;

  leads.forEach((lead) => {
    if (lead.remainder_date && lead.remainder_time && !lead.archieve) {
      scheduleCrmReminder(lead);
    }
  });
}

export function scheduleCrmReminderAfterSave(lead, payload) {
  const merged = {
    ...lead,
    remainder_date: payload.remainder_date ?? lead.remainder_date,
    remainder_time: payload.remainder_time ?? lead.remainder_time,
    followup_description: payload.followup_description ?? lead.followup_description,
  };

  cancelCrmReminder(merged.id);
  scheduleCrmReminder(merged);
}
